/**
 * CallSocket - WebSocket signaling management for PeersCaller
 * Handles all WebSocket communication for call signaling
 */

import { io, Socket } from "socket.io-client";
import type { SignalingEvents, SignalingIncomingEvents } from "../types";

export class CallSocket {
  private socket: Socket | null = null;
  private serverUrl: string;
  private socketPath: string;
  private token: string;
  private connected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  constructor(
    serverUrl: string,
    token: string,
    socketPath = "/apis/video-call"
  ) {
    this.serverUrl = serverUrl;
    this.token = token;
    this.socketPath = socketPath;
  }

  /**
   * Connect to the WebSocket server
   */
  async connect(): Promise<void> {
    // If already connected, don't create new connection
    if (this.socket && this.socket.connected) {
      return Promise.resolve();
    }

    // Clean up previous connection if exists
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    return new Promise((resolve, reject) => {
      try {
        this.socket = io(this.serverUrl, {
          path: this.socketPath,
          transports: ["websocket", "polling"],
          timeout: 10000,
          forceNew: true, // Prevent connection reuse that might cause duplicates
          auth: {
            token: this.token,
          },
        });

        this.socket.on("connect", () => {
          this.connected = true;
          this.reconnectAttempts = 0;
          console.log("[CallSocket] Connected to signaling server");
          resolve();
        });

        this.socket.on("disconnect", (reason) => {
          this.connected = false;
          console.log("[CallSocket] Disconnected:", reason);
          this.handleDisconnection(reason);
        });

        this.socket.on("connect_error", (error) => {
          console.error("[CallSocket] Connection error:", error);
          this.reconnectAttempts++;

          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            reject(
              new Error(
                `Failed to connect after ${this.maxReconnectAttempts} attempts`
              )
            );
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.connected && this.socket?.connected === true;
  }

  /**
   * Emit a signaling event - fire and forget (no response expected)
   */
  emit<K extends keyof SignalingEvents>(
    event: K,
    data: Parameters<SignalingEvents[K]>[0]
  ): void {
    if (!this.isConnected()) {
      throw new Error("Socket not connected");
    }

    console.log(`[CallSocket] Emitting ${event}:`, data);
    this.socket?.emit(event, data, (res: any) => {
      if (res?.error) {
        console.error(`[CallSocket] Error in ${event}:`, res.error);
      }
    });
  }

  /**
   * Emit a signaling event and wait for response
   */
  async emitWithResponse<K extends keyof SignalingEvents>(
    event: K,
    data: Parameters<SignalingEvents[K]>[0],
    timeoutMs: number = 10000
  ): Promise<any> {
    if (!this.isConnected()) {
      throw new Error("Socket not connected");
    }

    console.log(`[CallSocket] Emitting ${event} with response:`, data);

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Timeout waiting for response to ${event}`));
      }, timeoutMs);

      console.log(`[CallSocket] about to emit: ${event}...`);

      this.socket?.emit(event, data, (response: any) => {
        clearTimeout(timeout);

        if (response?.error) {
          console.error(`[CallSocket] Error in ${event}:`, response.error);
          reject(new Error(response.error));
        } else {
          console.log(`[CallSocket] Response for ${event}:`, response);
          resolve(response);
        }
      });
    });
  }

  /**
   * Listen for an incoming signaling event (from server)
   */
  on<K extends keyof SignalingIncomingEvents>(
    event: K,
    listener: SignalingIncomingEvents[K]
  ): void {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    // Register directly with socket - PeersCaller handles its own listener tracking
    this.socket.on(event, listener as any);
  }

  /**
   * Remove an incoming signaling event listener
   */
  off<K extends keyof SignalingIncomingEvents>(
    event: K,
    listener?: SignalingIncomingEvents[K]
  ): void {
    if (!this.socket) return;

    if (listener) {
      // Remove specific listener
      this.socket.off(event, listener as any);
    } else {
      // Remove all listeners for this event
      this.socket.removeAllListeners(event);
    }
  }

  /**
   * Start a new call
   */
  async startCall(
    conversationId: string,
    userId: string
  ): Promise<{
    success: boolean;
    participants: string[];
    conversationId: string;
    userId: string;
  }> {
    return this.emitWithResponse("call.start", { conversationId, userId });
  }

  /**
   * Join a call room for signaling
   */
  async joinRoom(
    conversationId: string,
    userId: string
  ): Promise<{ participants: string[]; conversationId: string }> {
    return this.emitWithResponse("call.join", { conversationId, userId });
  }

  /**
   * Leave a call
   */
  async leaveCall(
    conversationId: string,
    userId: string
  ): Promise<{ conversationId: string; success: boolean }> {
    return this.emitWithResponse("call.leave", { conversationId, userId });
  }

  /**
   * Send WebRTC offer
   */
  sendOffer(
    to: string,
    from: string,
    offer: RTCSessionDescriptionInit,
    conversationId: string
  ): void {
    this.emit("call.offer", { to, from, offer, conversationId });
  }

  /**
   * Send WebRTC answer
   */
  sendAnswer(
    to: string,
    from: string,
    answer: RTCSessionDescriptionInit,
    conversationId: string
  ): void {
    this.emit("call.answer", { to, from, answer, conversationId });
  }

  /**
   * Send ICE candidate
   */
  sendCandidate(
    to: string,
    from: string,
    candidate: RTCIceCandidateInit,
    conversationId: string
  ): void {
    this.emit("call.candidate", { to, from, candidate, conversationId });
  }

  /**
   * Send participant state update
   */
  sendStateUpdate(
    to: string,
    from: string,
    state: any,
    conversationId: string
  ): void {
    this.emit("call.state", { to, from, state, conversationId });
  }

  /**
   * End call for specific participant (legacy method)
   */
  endCall(to: string, _from: string, conversationId: string): void {
    // For backwards compatibility, end call for specific user
    this.emit("call.end", { conversationId, targetUserId: to });
  }

  /**
   * End call for all participants
   */
  async endCallForAll(conversationId: string): Promise<void> {
    this.emit("call.end", { conversationId });
  }

  /**
   * Start recording
   */
  async startRecording(
    conversationId: string,
    recordingId: string
  ): Promise<{ recordingId: string; conversationId: string }> {
    return this.emitWithResponse("call.recording.start", {
      conversationId,
      recordingId,
    });
  }

  /**
   * Send recording chunk
   */
  sendRecordingChunk(
    conversationId: string,
    recordingId: string,
    chunk: Blob
  ): void {
    this.emit("call.recording.chunk", { conversationId, recordingId, chunk });
  }

  /**
   * End recording
   */
  async endRecording(
    conversationId: string,
    recordingId: string
  ): Promise<{
    recordingId: string;
    conversationId: string;
    success: boolean;
  }> {
    return this.emitWithResponse("call.recording.end", {
      conversationId,
      recordingId,
    });
  }

  /**
   * Send transcript
   */
  sendTranscript(
    conversationId: string,
    userId: string,
    transcript: string
  ): void {
    this.emit("call.transcript", {
      conversationId,
      userId,
      transcript,
      timestamp: Date.now(),
    });
  }

  /**
   * Check call status
   */
  async checkCallStatus(conversationId: string, userId: string): Promise<any> {
    return this.emitWithResponse("call.status", { conversationId, userId });
  }

  /**
   * Handle disconnection scenarios
   */
  private handleDisconnection(reason: string): void {
    // Attempt to reconnect for certain disconnect reasons
    const shouldReconnect = [
      "transport close",
      "transport error",
      "io server disconnect",
    ].includes(reason);

    if (shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
      setTimeout(() => {
        if (!this.connected) {
          console.log(
            `[CallSocket] Attempting to reconnect... (${
              this.reconnectAttempts + 1
            }/${this.maxReconnectAttempts})`
          );
          this.connect().catch(console.error);
        }
      }, Math.pow(2, this.reconnectAttempts) * 1000); // Exponential backoff
    }
  }

  /**
   * Get connection health info
   */
  getConnectionInfo() {
    return {
      connected: this.connected,
      socketId: this.socket?.id,
      transport: this.socket?.io.engine.transport.name,
      reconnectAttempts: this.reconnectAttempts,
    };
  }
}
